import { v4 as uuidv4 } from "uuid";

const GUEST_USER_ID_KEY = "mix_guest_user_id";
const GUEST_EMAIL_KEY = "mix_guest_user_email";

/**
 * Get or create a persistent guest user ID
 * This ID is stored in localStorage and reused across sessions
 * to maintain vote history for guest users
 */
export function getOrCreateGuestUserId(): string {
    try {
        // Try to get existing guest user ID
        const existingId = localStorage.getItem(GUEST_USER_ID_KEY);
        if (existingId) {
            return existingId;
        }

        // Generate new UUID for guest user
        const newGuestId = uuidv4();
        localStorage.setItem(GUEST_USER_ID_KEY, newGuestId);
        return newGuestId;
    } catch (error) {
        console.error("Failed to get/create guest user ID:", error);
        // Fallback: generate temporary ID (won't persist)
        return uuidv4();
    }
}

/**
 * Generate a random 4-character alphanumeric string for guest email
 * Uses combination of numbers and lowercase letters (e.g., "4a9c", "1o2a", "2e56")
 */
function generateRandomGuestCode(): string {
    const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
    let result = "";
    for (let i = 0; i < 4; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
}

/**
 * Get or create a persistent guest user email
 * Format: guest_{4-char-code}@mix.day (e.g., guest_4a9c@mix.day)
 * This email is stored in localStorage and reused across sessions
 */
export function getOrCreateGuestEmail(): string {
    try {
        // Try to get existing guest email
        const existingEmail = localStorage.getItem(GUEST_EMAIL_KEY);
        if (existingEmail) {
            return existingEmail;
        }

        // Generate new email with random code
        const randomCode = generateRandomGuestCode();
        const newEmail = `guest_${randomCode}@mix.day`;
        localStorage.setItem(GUEST_EMAIL_KEY, newEmail);
        return newEmail;
    } catch (error) {
        console.error("Failed to get/create guest email:", error);
        // Fallback: generate temporary email (won't persist)
        return `guest_${generateRandomGuestCode()}@mix.day`;
    }
}

/**
 * Clear guest user ID (useful for testing or reset scenarios)
 */
export function clearGuestUserId(): void {
    try {
        localStorage.removeItem(GUEST_USER_ID_KEY);
        localStorage.removeItem(GUEST_EMAIL_KEY);
    } catch (error) {
        console.error("Failed to clear guest user ID:", error);
    }
}
